import { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  MapPin,
  Loader2,
  Layers,
  Wrench,
  MessageSquare,
  ClipboardList,
  User,
  Mail,
  Phone,
  Pencil,
  Info,
} from "lucide-react";
import { pt } from "date-fns/locale";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { WizardShell, OptionCard, PrimaryBtn, BackBtn, IdentityFields } from "@/components/mockups/WizardUI";
import SuccessModal from "@/components/mockups/SuccessModal";
import { publicApi, submitInbox, extractError, fetchServiceStatus } from "@/lib/publicApi";
import { resolveImage } from "@/hooks/useCms";
import SuspendedNotice from "@/components/SuspendedNotice";
import Honeypot, { honeypotValue } from "@/components/Honeypot";
import {
  validateIdentity,
  focusFirstError,
  buildErrorSummary,
  formatPostalPT,
  formatPhonePT,
  isValidPostalPT,
} from "@/lib/formValidation";
import { FieldError, errorInputClass } from "@/components/mockups/WizardUI";
import { usePostalAutofill } from "@/lib/usePostalAutofill";

// Gera os próximos 10 dias úteis (excluindo fins de semana) - mantido para fallback do label
const buildDays = () => {
  const days = [];
  const d = new Date();
  while (days.length < 10) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(new Date(d));
  }
  return days;
};

// Data mínima = amanhã; máxima = daqui a 90 dias
const buildDateBounds = () => {
  const min = new Date();
  min.setHours(0, 0, 0, 0);
  min.setDate(min.getDate() + 1);
  const max = new Date();
  max.setHours(0, 0, 0, 0);
  max.setDate(max.getDate() + 90);
  return { min, max };
};

// Verifica se uma data é fim-de-semana
const isWeekend = (d) => d && (d.getDay() === 0 || d.getDay() === 6);

const fmtDay = (d) =>
  d ? d.toLocaleDateString("pt-PT", { weekday: "short", day: "numeric", month: "short" }) : "";

const TOTAL_STEPS = 5;
const OTHER_STEP = 90; // ecrã especial para pedidos "Outro / não listado"
const OTHER_SOLUTION = {
  id: "__other__",
  slug: "outro",
  title: "Outro / Não listado",
  short_desc: "Quero saber mais sobre um setor que não está na lista",
  isOther: true,
};
const OTHER_SERVICE = {
  id: "__other__",
  slug: "outro",
  title: "Outro / Não listado",
  short_desc: "Quero saber mais sobre um serviço que não está na lista",
  isOther: true,
};

export default function AgendamentoDialog({ open, onClose, prefill = null }) {
  const [step, setStep] = useState(0);
  const [solutions, setSolutions] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState("");

  const [solution, setSolution] = useState(null);
  const [servico, setServico] = useState(null);
  const [dia, setDia] = useState(null);
  const [observacoes, setObservacoes] = useState("");
  const [identidade, setIdentidade] = useState({
    nome: "",
    email: "",
    telefone: "",
    codigo_postal: "",
    morada: "",
  });
  const [sending, setSending] = useState(false);
  // anti-spam (honeypot) — o passo de identidade desmonta antes do envio
  const hpRef = useRef(null);
  const hpValRef = useRef("");
  const [successData, setSuccessData] = useState(null);
  const [touched, setTouched] = useState({});
  const [otherText, setOtherText] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  // Marcado true quando o utilizador edita a morada manualmente ou quando esta
  // vem por prefill do orçamento — impede que o auto-fill CP sobreponha.
  const [moradaUserEdited, setMoradaUserEdited] = useState(false);
  // Contexto herdado do wizard de orçamento — tipologia + respostas às perguntas
  // complementares. Não editável neste wizard (é só informação de referência),
  // aparece no ecrã de revisão e no SuccessModal para dar continuidade ao pedido.
  const [inheritedContext, setInheritedContext] = useState(null); // { typology_label, typology_value, answers_labelled }
  const otherMode = !!(solution?.isOther || servico?.isOther);
  const days = useMemo(buildDays, []);
  const dateBounds = useMemo(buildDateBounds, []);

  // Auto-preenche a "Morada" a partir do Código Postal (base CTT).
  const setMoradaOnly = (val) => setIdentidade((prev) => ({ ...prev, morada: val }));
  const { loading: postalLoading } = usePostalAutofill(
    identidade.codigo_postal,
    identidade.morada,
    setMoradaOnly,
    { userEdited: moradaUserEdited, enabled: open },
  );

  // ─── suspensão do serviço ─────────────────────────────────────────────────
  const [suspended, setSuspended] = useState(null);
  useEffect(() => {
    if (!open) {
      setSuspended(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const status = await fetchServiceStatus();
      if (cancelled) return;
      const svc = status?.agendamento;
      if (svc?.suspended) setSuspended({ title: svc.title, message: svc.message });
      else setSuspended(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Cálculo de erros do step de identificação (nome/email/telefone/CP obrigatórios)
  const identityErrors = useMemo(() => {
    const { errors } = validateIdentity(identidade, { phoneRequired: true, postalRequired: true });
    return errors;
  }, [identidade]);
  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const errFor = (field) => (touched[field] ? identityErrors[field] : "");

  // Erros de identidade para o fluxo "Outro" (CP não obrigatório — é só um pedido de info)
  const otherIdentityErrors = useMemo(() => {
    return validateIdentity(identidade, { phoneRequired: true, postalRequired: false }).errors;
  }, [identidade]);

  // Carrega soluções + serviços quando o dialog abre pela primeira vez
  useEffect(() => {
    if (!open || solutions.length > 0) return;
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      setCatalogError("");
      try {
        const [solRes, svcRes] = await Promise.all([
          publicApi.get("/solutions"),
          publicApi.get("/services"),
        ]);
        if (cancelled) return;
        const sols = (solRes.data || []).filter((s) => s.published !== false);
        const svcs = (svcRes.data || []).filter((s) => s.published !== false);
        setSolutions(sols);
        setServices(svcs);
      } catch (e) {
        if (!cancelled) setCatalogError("Não foi possível carregar as soluções.");
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, solutions.length]);

  const servicesOfSolution = useMemo(() => {
    if (!solution) return [];
    const ids = solution.service_ids || [];
    if (ids.length === 0) return services;
    const byId = new Map(services.map((s) => [s.id, s]));
    return ids.map((sid) => byId.get(sid)).filter(Boolean);
  }, [solution, services]);

  // ─── Aplicar prefill quando dialog abre com dados vindos do Orçamento ────
  // Corre assim que o catálogo tenha carregado. Só aplica uma vez por abertura.
  useEffect(() => {
    if (!open) {
      setPrefilled(false);
      return;
    }
    if (!prefill) return;
    if (prefilled) return;
    if (solutions.length === 0 || services.length === 0) return;
    // Localizar solução por id ou slug
    const sol =
      solutions.find(
        (s) => s.id === prefill.solution_id || s.slug === prefill.solution_slug,
      ) || null;
    const svc =
      services.find(
        (s) => s.id === prefill.servico_id || s.slug === prefill.servico_slug,
      ) || null;
    if (sol) setSolution(sol);
    if (svc) setServico(svc);
    setIdentidade((prev) => ({
      ...prev,
      nome: prefill.nome || prev.nome || "",
      email: prefill.email || prev.email || "",
      telefone: prefill.telefone || prev.telefone || "",
      codigo_postal: prefill.codigo_postal
        ? formatPostalPT(prefill.codigo_postal)
        : prev.codigo_postal || "",
      morada: prefill.morada || prev.morada || "",
    }));
    // Se veio morada do prefill, marca como "editada" para não sobrepor
    if (prefill.morada) setMoradaUserEdited(true);
    // Salta para o passo "Data" (2) se tudo essencial estiver identificado
    if (sol && svc) setStep(2);
    else if (sol) setStep(1);
    // Guardar tipologia + respostas herdadas do orçamento (se vierem no prefill)
    if (prefill.typology_label || prefill.typology_value || prefill.answers_labelled || prefill.transport_surcharge != null || prefill.origin_postal_code || prefill.origin_place || prefill.service_note || prefill.typology_note) {
      setInheritedContext({
        typology_label: prefill.typology_label || "",
        typology_value: prefill.typology_value || "",
        typology_id: prefill.typology_id || "",
        answers_labelled: prefill.answers_labelled || {},
        transport_surcharge: prefill.transport_surcharge ?? null,
        transport_km: prefill.transport_km ?? null,
        transport_tier: prefill.transport_tier || "",
        // Ponto de partida do técnico — usado apenas no relatório do admin
        origin_postal_code: prefill.origin_postal_code || "",
        origin_place: prefill.origin_place || "",
        destination_place: prefill.destination_place || "",
        // Textos livres herdados do orçamento
        service_note: prefill.service_note || "",
        typology_note: prefill.typology_note || "",
      });
    } else {
      setInheritedContext(null);
    }
    setPrefilled(true);
  }, [open, prefill, prefilled, solutions, services]);

  const resetState = () => {
    setStep(0);
    setSolution(null);
    setServico(null);
    setDia(null);
    setObservacoes("");
    setIdentidade({ nome: "", email: "", telefone: "", codigo_postal: "", morada: "" });
    setMoradaUserEdited(false);
    setSending(false);
    setTouched({});
    setOtherText("");
    setPrefilled(false);
    setInheritedContext(null);
  };

  const reset = () => {
    resetState();
    onClose();
  };

  const identityValid = Object.keys(identityErrors).length === 0;

  // Handler ao clicar em "Ver Resumo": se inválido, marca todos os campos
  // obrigatórios como touched (mostra os erros), foca o primeiro campo com
  // erro e dispara toast. Só avança se estiver tudo válido.
  const handleIdentitySubmit = () => {
    const { isValid, errors, firstErrorField } = validateIdentity(identidade, {
      phoneRequired: true,
      postalRequired: true,
    });
    if (!isValid) {
      setTouched({ nome: true, email: true, telefone: true, codigo_postal: true });
      toast.error(buildErrorSummary(errors));
      focusFirstError(firstErrorField, "agd");
      return;
    }
    setStep(4);
  };

  const finish = async () => {
    if (sending) return;
    setSending(true);
    try {
      const dataIso = dia ? dia.toISOString().slice(0, 10) : "";
      const solLabel = solution?.title || "";
      const svcLabel = servico?.title || "";
      // Linhas de contexto herdado do orçamento (tipologia + perguntas complementares)
      // — só aparecem se este agendamento veio via "Agendar Online agora" do
      // wizard de orçamento. Não afectam agendamentos abertos directamente.
      const inheritedLines = [];
      const ic = inheritedContext;
      if (ic?.typology_label && ic?.typology_value) {
        inheritedLines.push(`• ${ic.typology_label}: ${ic.typology_value}`);
      }
      if (ic?.answers_labelled) {
        Object.entries(ic.answers_labelled).forEach(([k, v]) => {
          if (ic.typology_label && k === ic.typology_label) return; // já incluído acima
          inheritedLines.push(`• ${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
        });
      }
      const messageLines = [
        "Agendamento pretendido:",
        `• Solução: ${solLabel}`,
        `• Serviço: ${svcLabel}`,
        ...inheritedLines,
        `• Data: ${fmtDay(dia)}`,
        `• Código Postal: ${identidade.codigo_postal}`,
      ];
      if (identidade.morada.trim()) messageLines.push(`• Morada: ${identidade.morada.trim()}`);
      if (observacoes.trim()) messageLines.push(`• Observações do cliente: ${observacoes.trim()}`);
      // Textos livres herdados do orçamento
      const _typNote = (ic?.typology_note || "").trim();
      const _svcNote = (ic?.service_note || "").trim();
      if (_typNote) messageLines.push(`• Observações: ${_typNote}`);
      if (_svcNote) messageLines.push(`• Nota: ${_svcNote}`);
      await submitInbox({
        type: "agendamento",
        name: identidade.nome,
        email: identidade.email,
        phone: identidade.telefone,
        subject: `Agendamento · ${solLabel} · ${svcLabel} · ${fmtDay(dia)}`,
        message: messageLines.join("\n"),
        extra: {
          website: honeypotValue(hpRef, hpValRef),
          solution_id: solution?.id,
          solution_slug: solution?.slug,
          solution_title: solLabel,
          servico_id: servico?.id,
          servico_slug: servico?.slug,
          servico_label: svcLabel,
          codigo_postal: identidade.codigo_postal,
          morada: identidade.morada.trim() || null,
          observacoes: observacoes.trim() || null,
          data_pretendida: dataIso,
          data_pretendida_label: fmtDay(dia),
          // Marcador: pedido de agendamento originado no wizard de orçamento
          // (aplica-se sempre que o utilizador chegou aqui via prefill, mesmo
          // que tenha corrigido dados no meio — conforme regra do produto).
          from_orcamento: !!ic,
          // Contexto herdado do orçamento — permite ao admin ver o pedido completo
          typology_id: ic?.typology_id || null,
          typology_label: ic?.typology_label || null,
          typology_value: ic?.typology_value || null,
          answers_labelled: ic?.answers_labelled || null,
          transport_surcharge: ic?.transport_surcharge ?? null,
          transport_km: ic?.transport_km ?? null,
          transport_tier: ic?.transport_tier || null,
          // Ponto de partida do técnico — herdado do orçamento anterior.
          // Fica no `extra` para o relatório admin (SubmissionsAdmin +
          // enriquecimento do email de notificação interna). Nunca aparece
          // no email do cliente.
          origin_postal_code: ic?.origin_postal_code || null,
          origin_place: ic?.origin_place || null,
          destination_place: ic?.destination_place || null,
          // Textos livres herdados do orçamento — surgem no SuccessModal e emails
          service_note: (ic?.service_note || "").trim() || null,
          typology_note: (ic?.typology_note || "").trim() || null,
        },
      });
      // Mostrar modal de sucesso INXPEK e fechar o wizard
      setSuccessData({
        name: identidade.nome,
        email: identidade.email,
        details: [
          { icon: Layers, label: "Solução", value: solLabel },
          { icon: Wrench, label: "Serviço", value: svcLabel },
          ...(ic?.typology_label && ic?.typology_value
            ? [{ icon: ClipboardList, label: ic.typology_label, value: ic.typology_value }]
            : []),
          // Respostas complementares (excluindo a tipologia já mostrada)
          ...(ic?.answers_labelled
            ? Object.entries(ic.answers_labelled)
                .filter(([k]) => !ic?.typology_label || k !== ic.typology_label)
                .map(([k, v]) => ({
                  icon: ClipboardList,
                  label: k,
                  value: Array.isArray(v) ? v.join(", ") : String(v),
                }))
            : []),
          { icon: CalendarDays, label: "Data pretendida", value: fmtDay(dia) },
          { icon: MapPin, label: "Código postal", value: identidade.codigo_postal },
          ...(identidade.morada.trim()
            ? [{ icon: MapPin, label: "Morada", value: identidade.morada.trim(), multiline: true }]
            : []),
          // Observações (por tipologia herdada do orçamento)
          ...((ic?.typology_note || "").trim()
            ? [{ icon: ClipboardList, label: "Observações", value: ic.typology_note.trim(), multiline: true }]
            : []),
          // Nota (por serviço herdada do orçamento)
          ...((ic?.service_note || "").trim()
            ? [{ icon: Info, label: "Nota", value: ic.service_note.trim(), multiline: true }]
            : []),
        ],
        // Frase de origem — mostrada no SuccessModal quando o agendamento
        // veio do wizard de orçamento (regra: sempre que ic exista).
        origin_orcamento: !!ic,
      });
      onClose(); // fecha o wizard; SuccessModal continua visível (state próprio)
    } catch (err) {
      toast.error(extractError(err, "Não foi possível registar o agendamento. Tente novamente."));
      setSending(false);
    }
  };

  // Submissão do fluxo "Outro / não listado"
  const finishOther = async () => {
    if (sending) return;
    if (!otherText || otherText.trim().length < 5) {
      toast.error("Descreva o que pretende (mínimo 5 caracteres).");
      return;
    }
    const { isValid, errors, firstErrorField } = validateIdentity(identidade, {
      phoneRequired: true,
      postalRequired: false,
    });
    if (!isValid) {
      setTouched({ nome: true, email: true, telefone: true });
      toast.error(buildErrorSummary(errors));
      focusFirstError(firstErrorField, "agd");
      return;
    }
    setSending(true);
    try {
      const solLabel = solution?.isOther ? "Outro / Não listado" : solution?.title || "";
      const svcLabel = servico?.isOther ? "Outro / Não listado" : servico?.title || "";
      const messageLines = [
        "Pedido de agendamento — solução/serviço não listado:",
        `• Solução: ${solLabel}`,
        `• Serviço: ${svcLabel}`,
        `• Descrição do cliente: ${otherText.trim()}`,
      ];
      await submitInbox({
        type: "agendamento",
        name: identidade.nome,
        email: identidade.email,
        phone: identidade.telefone,
        subject: "Agendamento · Outro (não listado)",
        message: messageLines.join("\n"),
        extra: {
          website: honeypotValue(hpRef, hpValRef),
          is_other: true,
          solution_id: solution?.isOther ? null : solution?.id,
          solution_slug: solution?.isOther ? null : solution?.slug,
          solution_title: solLabel,
          servico_id: servico?.isOther ? null : servico?.id,
          servico_slug: servico?.isOther ? null : servico?.slug,
          servico_label: svcLabel,
          other_description: otherText.trim(),
        },
      });
      setSuccessData({
        name: identidade.nome,
        email: identidade.email,
        details: [
          { icon: Layers, label: "Solução", value: solLabel },
          { icon: Wrench, label: "Serviço", value: svcLabel },
          { icon: MessageSquare, label: "O seu pedido", value: otherText.trim() },
        ],
      });
      onClose();
      resetState();
    } catch (err) {
      toast.error(extractError(err, "Não foi possível registar o pedido. Tente novamente."));
      setSending(false);
    }
  };

  // Descrição dinâmica por passo
  const stepDesc = (() => {
    switch (step) {
      case 0:
        return "Comece por escolher o setor que se aplica ao seu caso.";
      case 1:
        return `Escolha o serviço que pretende agendar${solution ? ` em ${solution.title}` : ""}.`;
      case 2:
        return "Selecione o dia pretendido e, se quiser, deixe uma observação.";
      case 3:
        return "Preencha os seus dados para podermos confirmar o agendamento.";
      case 4:
        return "Reveja os dados. Pode editar qualquer secção antes de confirmar.";
      case OTHER_STEP:
        return "Descreva o que pretende e deixe os seus dados. A nossa equipa entra em contacto.";
      default:
        return "";
    }
  })();

  // Footer sticky por passo
  const footer = (() => {
    if (step === 0) {
      return (
        <PrimaryBtn
          testId="agd-next-0"
          disabled={!solution || loadingCatalog}
          onClick={() => setStep(solution?.isOther ? OTHER_STEP : 1)}
        >
          Continuar
        </PrimaryBtn>
      );
    }
    if (step === 1) {
      return (
        <>
          <BackBtn testId="agd-back-1" onClick={() => setStep(0)} />
          <PrimaryBtn
            testId="agd-next-1"
            disabled={!servico}
            onClick={() => setStep(servico?.isOther ? OTHER_STEP : 2)}
          >
            Continuar
          </PrimaryBtn>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <BackBtn testId="agd-back-2" onClick={() => setStep(1)} />
          <PrimaryBtn testId="agd-next-2" disabled={!dia} onClick={() => setStep(3)}>
            Continuar
          </PrimaryBtn>
        </>
      );
    }
    if (step === 3) {
      return (
        <>
          <BackBtn testId="agd-back-3" onClick={() => setStep(2)} />
          <PrimaryBtn
            testId="agd-next-3"
            onClick={handleIdentitySubmit}
          >
            Ver Resumo
          </PrimaryBtn>
        </>
      );
    }
    if (step === 4) {
      return (
        <>
          <BackBtn testId="agd-back-4" onClick={() => setStep(3)} />
          <PrimaryBtn testId="agd-submit" disabled={sending} onClick={finish}>
            {sending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> A enviar…
              </span>
            ) : (
              "Confirmar Agendamento"
            )}
          </PrimaryBtn>
        </>
      );
    }
    if (step === OTHER_STEP) {
      return (
        <>
          <BackBtn
            testId="agd-back-other"
            onClick={() => setStep(solution?.isOther ? 0 : 1)}
          />
          <PrimaryBtn testId="agd-submit-other" disabled={sending} onClick={finishOther}>
            {sending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> A enviar…
              </span>
            ) : (
              "Enviar Pedido"
            )}
          </PrimaryBtn>
        </>
      );
    }
    return null;
  })();

  return (
    <>
      <WizardShell
        open={open}
        onClose={reset}
        testId="dialog-agendamento"
        step={suspended ? 1 : step === OTHER_STEP ? (solution?.isOther ? 1 : 2) : step}
        total={suspended ? 1 : step === OTHER_STEP ? (solution?.isOther ? 2 : 3) : TOTAL_STEPS}
        title="Agendar Online"
        desc={suspended ? "" : stepDesc}
        footer={suspended ? null : footer}
      >
      {suspended ? (
        <SuspendedNotice
          title={suspended.title}
          message={suspended.message}
          onClose={reset}
          testId="agd-suspended"
        />
      ) : (
        <>
      {/* STEP 0 — Solução */}
      {step === 0 && (
        <div className="space-y-4">
          {loadingCatalog ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 size={18} className="animate-spin mr-2" /> A carregar soluções…
            </div>
          ) : catalogError ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {catalogError}
            </div>
          ) : solutions.length === 0 ? (
            <div className="text-sm text-slate-500 dark:text-slate-300 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg p-3">
              Sem soluções disponíveis de momento.
            </div>
          ) : (
            <div
              data-lenis-prevent
              className="grid gap-2 max-h-72 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-visible pr-1"
              data-testid="agd-solutions-grid"
            >
              {[...solutions, OTHER_SOLUTION].map((s) => (
                <OptionCard
                  key={s.id}
                  testId={`agd-solucao-${s.slug}`}
                  selected={solution?.id === s.id}
                  onClick={() => {
                    setSolution(s);
                    setServico(null);
                  }}
                  label={s.title}
                  desc={s.short_desc}
                  image={!s.isOther ? resolveImage(s.image) : ""}
                  icon={Layers}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 1 — Serviço (sem código postal) */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="text-xs bg-[#1698AC]/8 dark:bg-[#4FC6D8]/10 border border-[#1698AC]/20 dark:border-[#4FC6D8]/25 text-[#0F7484] dark:text-[#4FC6D8] rounded-lg px-3 py-2 flex items-center gap-2">
            <Layers size={12} /> Solução: <strong>{solution?.title}</strong>
          </div>
          {servicesOfSolution.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-slate-300 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg p-3">
              Esta solução ainda não tem serviços listados. Escolha “Outro” abaixo para nos dizer o que precisa.
            </div>
          )}
          <div
            data-lenis-prevent
            className="grid gap-2 max-h-80 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-visible pr-1"
            data-testid="agd-services-grid"
          >
            {[...servicesOfSolution, OTHER_SERVICE].map((s) => (
              <OptionCard
                key={s.id}
                testId={`agd-servico-${s.slug}`}
                selected={servico?.id === s.id}
                onClick={() => setServico(s)}
                label={s.title}
                desc={s.short_desc}
                image={!s.isOther ? resolveImage(s.image) : ""}
                icon={Wrench}
              />
            ))}
          </div>
        </div>
      )}

      {/* STEP 2 — Data + Observações (sem hora) */}
      {step === 2 && (
        <div className="space-y-5">
          {prefilled && (
            <div
              data-testid="agd-prefill-banner"
              className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 flex items-start gap-2"
            >
              <CalendarDays size={13} className="mt-0.5 shrink-0" />
              <span>
                Os seus dados vieram do orçamento anterior — só falta escolher o dia.
                Pode ajustar qualquer detalhe voltando aos passos anteriores.
              </span>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <CalendarDays size={13} className="text-[#1698AC]" /> Dia pretendido
              {dia && (
                <span className="ml-2 normal-case tracking-normal text-slate-700 dark:text-slate-200 font-medium">
                  · <span className="capitalize">{fmtDay(dia)}</span>
                </span>
              )}
            </p>
            <div
              className="rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-1 flex justify-center"
              data-testid="agd-calendar-wrapper"
            >
              <Calendar
                mode="single"
                selected={dia || undefined}
                onSelect={(d) => setDia(d || null)}
                fromDate={dateBounds.min}
                toDate={dateBounds.max}
                disabled={(d) => isWeekend(d) || d < dateBounds.min || d > dateBounds.max}
                locale={pt}
                weekStartsOn={1}
                showOutsideDays={false}
                compact
                data-testid="agd-calendar"
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-white/25" /> Fins de semana e datas anteriores estão indisponíveis
            </p>
          </div>
          <div className="space-y-1.5">
            <Label
              htmlFor="agd-observacoes"
              className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"
            >
              <MessageSquare size={13} className="text-[#1698AC]" /> Observações{" "}
              <span className="text-[10px] font-normal normal-case tracking-normal text-slate-400">
                (opcional)
              </span>
            </Label>
            <Textarea
              id="agd-observacoes"
              data-testid="agd-observacoes-input"
              rows={2}
              placeholder="Ex.: preferência de horário, acessos, condições do local, urgência…"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              className="resize-none"
            />
            <p className="text-[11px] text-slate-400">
              A nossa equipa entrará em contacto para confirmar hora e detalhes específicos.
            </p>
          </div>
        </div>
      )}

      {/* STEP 3 — Identificação (nome/email/telefone/CP obrigatórios + morada opcional) */}
      {step === 3 && (
        <div className="space-y-3" data-testid="agd-identity-form">
          <Honeypot inputRef={hpRef} valueRef={hpValRef} />
          <div className="space-y-1.5">
            <Label htmlFor="agd-nome" className="flex items-center gap-1.5">
              <User size={12} className="text-[#1698AC]" /> Nome <span className="text-red-500">*</span>
            </Label>
            <Input
              id="agd-nome"
              data-testid="agd-nome-input"
              required
              aria-invalid={!!errFor("nome")}
              aria-describedby={errFor("nome") ? "agd-nome-error" : undefined}
              placeholder="O seu nome"
              value={identidade.nome}
              onChange={(e) => setIdentidade({ ...identidade, nome: e.target.value })}
              onBlur={() => markTouched("nome")}
              className={errorInputClass(!!errFor("nome"))}
            />
            <FieldError id="agd-nome-error" message={errFor("nome")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="agd-email" className="flex items-center gap-1.5">
                <Mail size={12} className="text-[#1698AC]" /> Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="agd-email"
                data-testid="agd-email-input"
                type="email"
                required
                aria-invalid={!!errFor("email")}
                aria-describedby={errFor("email") ? "agd-email-error" : undefined}
                placeholder="o.seu@email.pt"
                value={identidade.email}
                onChange={(e) => setIdentidade({ ...identidade, email: e.target.value })}
                onBlur={() => markTouched("email")}
                className={errorInputClass(!!errFor("email"))}
              />
              <FieldError id="agd-email-error" message={errFor("email")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agd-telefone" className="flex items-center gap-1.5">
                <Phone size={12} className="text-[#1698AC]" /> Telefone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="agd-telefone"
                data-testid="agd-telefone-input"
                type="tel"
                required
                aria-invalid={!!errFor("telefone")}
                aria-describedby={errFor("telefone") ? "agd-telefone-error" : undefined}
                placeholder="912 345 678"
                maxLength={20}
                value={identidade.telefone}
                onChange={(e) =>
                  setIdentidade({ ...identidade, telefone: formatPhonePT(e.target.value) })
                }
                onBlur={() => markTouched("telefone")}
                className={errorInputClass(!!errFor("telefone"))}
              />
              <FieldError id="agd-telefone-error" message={errFor("telefone")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agd-cp" className="flex items-center gap-1.5">
              <MapPin size={12} className="text-[#1698AC]" /> Código postal do local <span className="text-red-500">*</span>
            </Label>
            <Input
              id="agd-cp"
              data-testid="agd-cp-input"
              required
              inputMode="numeric"
              maxLength={8}
              aria-invalid={!!errFor("codigo_postal")}
              aria-describedby={errFor("codigo_postal") ? "agd-cp-error" : undefined}
              placeholder="0000-000"
              value={identidade.codigo_postal}
              onChange={(e) =>
                setIdentidade({ ...identidade, codigo_postal: formatPostalPT(e.target.value) })
              }
              onBlur={() => markTouched("codigo_postal")}
              className={errorInputClass(!!errFor("codigo_postal"))}
            />
            <FieldError id="agd-cp-error" message={errFor("codigo_postal")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agd-morada" className="flex items-center gap-1.5">
              <MapPin size={12} className="text-[#1698AC]" /> Morada do local da inspeção{" "}
              <span className="text-[10px] font-normal text-slate-400">(opcional)</span>
            </Label>
            <Textarea
              id="agd-morada"
              data-testid="agd-morada-input"
              rows={2}
              placeholder="Ex.: Rua da República, 123, 3.º Esq. — Marinha Grande"
              value={identidade.morada}
              onChange={(e) => {
                setIdentidade({ ...identidade, morada: e.target.value });
                setMoradaUserEdited(true);
              }}
              className="resize-none"
            />
            {postalLoading && (
              <p className="text-[11px] text-slate-500 dark:text-slate-300 leading-snug">
                A procurar morada do código postal…
              </p>
            )}
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
            Os seus dados são tratados de acordo com o RGPD, exclusivamente para resposta ao seu
            pedido.
          </p>
        </div>
      )}

      {/* STEP 4 — Resumo com edição por secção */}
      {step === 4 && (
        <div className="space-y-3" data-testid="agd-resumo">
          <SummaryRow
            icon={Layers}
            label="Solução"
            value={solution?.title}
            onEdit={() => setStep(0)}
            testId="agd-summary-solucao"
          />
          <SummaryRow
            icon={Wrench}
            label="Serviço"
            value={servico?.title}
            onEdit={() => setStep(1)}
            testId="agd-summary-servico"
          />
          {/* Contexto herdado do orçamento — tipologia + respostas complementares */}
          {inheritedContext?.typology_label && inheritedContext?.typology_value && (
            <SummaryRow
              icon={ClipboardList}
              label={inheritedContext.typology_label}
              value={inheritedContext.typology_value}
              testId="agd-summary-typology"
            />
          )}
          {inheritedContext?.answers_labelled &&
            Object.entries(inheritedContext.answers_labelled)
              .filter(([k]) => !inheritedContext?.typology_label || k !== inheritedContext.typology_label)
              .map(([k, v]) => (
                <SummaryRow
                  key={`agd-inh-${k}`}
                  icon={ClipboardList}
                  label={k}
                  value={Array.isArray(v) ? v.join(", ") : String(v)}
                  testId={`agd-summary-answer-${k.toLowerCase().replace(/\s+/g, "-")}`}
                />
              ))}
          <SummaryRow
            icon={CalendarDays}
            label="Data"
            value={fmtDay(dia)}
            valueClass="capitalize"
            onEdit={() => setStep(2)}
            testId="agd-summary-data"
          />
          {observacoes.trim() && (
            <SummaryRow
              icon={MessageSquare}
              label="Observações"
              value={observacoes.trim()}
              multiline
              onEdit={() => setStep(2)}
              testId="agd-summary-observacoes"
            />
          )}
          <SummaryRow
            icon={User}
            label="Nome"
            value={identidade.nome}
            onEdit={() => setStep(3)}
            testId="agd-summary-nome"
          />
          <SummaryRow
            icon={Mail}
            label="Email"
            value={identidade.email}
            onEdit={() => setStep(3)}
            testId="agd-summary-email"
          />
          <SummaryRow
            icon={Phone}
            label="Telefone"
            value={identidade.telefone}
            onEdit={() => setStep(3)}
            testId="agd-summary-telefone"
          />
          <SummaryRow
            icon={MapPin}
            label="Código postal"
            value={identidade.codigo_postal}
            onEdit={() => setStep(3)}
            testId="agd-summary-cp"
          />
          {identidade.morada.trim() && (
            <SummaryRow
              icon={MapPin}
              label="Morada do local"
              value={identidade.morada.trim()}
              multiline
              onEdit={() => setStep(3)}
              testId="agd-summary-morada"
            />
          )}
          <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
            Ao confirmar, será registado o seu pedido e receberá um email com o resumo. A nossa
            equipa entrará em contacto para confirmar a hora final e detalhes específicos.
          </p>
        </div>
      )}

      {/* STEP OUTRO — Descrição livre + Identidade */}
      {step === OTHER_STEP && (
        <form
          id="agd-form-other"
          onSubmit={(e) => {
            e.preventDefault();
            finishOther();
          }}
          className="space-y-4"
        >
          <Honeypot inputRef={hpRef} valueRef={hpValRef} />
          <div className="text-xs bg-[#1698AC]/8 dark:bg-[#1698AC]/15 border border-[#1698AC]/20 dark:border-[#1698AC]/40 text-[#0F7484] dark:text-white rounded-lg px-3 py-2 flex items-start gap-2">
            <MessageSquare size={13} className="mt-0.5 shrink-0" />
            <span>
              Como escolheu <strong>“Outro / Não listado”</strong>, descreva o que pretende e a
              nossa equipa entra em contacto para tratar do seu pedido.
            </span>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agd-other-desc">
              Descreva o que pretende <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="agd-other-desc"
              data-testid="agd-other-desc"
              rows={4}
              placeholder="Ex.: Preciso de uma inspeção/certificação para… (indique o tipo de instalação, localização e qualquer detalhe útil)"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              className="resize-none"
            />
          </div>
          <IdentityFields
            prefix="agd"
            values={identidade}
            onChange={setIdentidade}
            errors={otherIdentityErrors}
            touched={touched}
            onBlur={markTouched}
          />
        </form>
      )}
      </>
      )}
    </WizardShell>
      <SuccessModal
        open={!!successData}
        onClose={() => {
          setSuccessData(null);
          resetState();
        }}
        title="Agendamento registado!"
        eyebrow="AGENDAMENTO"
        name={successData?.name}
        email={successData?.email}
        details={successData?.details || []}
        originNotice={
          successData?.origin_orcamento
            ? "Este pedido de agendamento foi gerado a partir de um Orçamento online."
            : null
        }
        message="A nossa equipa confirmará a hora final e detalhes específicos por email nas próximas horas úteis."
        testId="agd-success-modal"
      />
    </>
  );
}

// ---------- helper: linha do resumo com botão "Editar" ----------
function SummaryRow({ icon: Icon, label, value, onEdit, multiline = false, valueClass = "", testId }) {
  return (
    <div
      className="border border-slate-200 dark:border-white/10 rounded-lg p-3 flex items-start gap-3 bg-white/60 dark:bg-white/5"
      data-testid={testId}
    >
      <div className="w-8 h-8 rounded-lg bg-[#1698AC]/10 text-[#1698AC] flex items-center justify-center shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-slate-400">
          {label}
        </p>
        <p
          className={`text-sm font-medium text-slate-800 dark:text-slate-100 ${
            multiline ? "whitespace-pre-wrap break-words" : "truncate"
          } ${valueClass}`}
        >
          {value || "—"}
        </p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        data-testid={`${testId}-edit`}
        className={
          "text-[11px] font-semibold hover:underline inline-flex items-center gap-1 shrink-0 mt-0.5 active:scale-95 transition-transform " +
          (onEdit ? "text-[#1698AC] hover:text-[#0F7484]" : "invisible pointer-events-none")
        }
        aria-label={onEdit ? `Editar ${label}` : undefined}
        aria-hidden={!onEdit}
        tabIndex={onEdit ? 0 : -1}
      >
        <Pencil size={11} /> Editar
      </button>
    </div>
  );
}
